import { Fragment, FormEvent, useState, useEffect } from "react";
import { IAdminMutationData, AdminMutationOptionsType } from "./adminUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useActivationContext } from "../LicenseActivationContext";
import { useDeltaToastContext } from "../../../../shared/contexts/ToasterContext";
import Button from "../../../../student/components/generic/button";
import { deltamathAPI } from "../../../utils";
import axios from "axios";
import {
  coerceLicenseType,
  discriminateInvoiceType,
} from "../../../utils/quoteUtils";

interface ICreateAdminProps {
  userDataForAdminMutation?: IAdminMutationData;
  showAddNewAdminForm: boolean;
  searchEmail: string;
  showAddExistingAdmin: boolean;
  updateAdmin: (params: any) => void;
  setMutationAction: React.Dispatch<
    React.SetStateAction<AdminMutationOptionsType>
  >;
  setRefetchInvoiceOnModalClose: React.Dispatch<React.SetStateAction<boolean>>;
}

const CreateAdmin: React.FC<ICreateAdminProps> = ({
  userDataForAdminMutation,
  showAddNewAdminForm,
  searchEmail,
  showAddExistingAdmin,
  updateAdmin,
  setMutationAction,
  setRefetchInvoiceOnModalClose,
}) => {
  const activationContext = useActivationContext();
  const toastContext = useDeltaToastContext();
  const queryClient = useQueryClient();

  const [newAdminPayload, setNewAdminPayload] = useState<{
    first: string;
    last: string;
  }>({ first: "", last: "" });

  const siteType = discriminateInvoiceType(
    coerceLicenseType(activationContext.invoiceDetails?.type || "")
  );

  //mutation for creating a "new" admin, whether actually new or taking an admin not associated w/ the site and adding them to the site
  const { mutate: createAdmin } = useMutation({
    mutationFn: async () => {
      const body = {
        siteType,
        siteId: activationContext.invoiceDetails?.id,
        userData: {
          firstName: showAddExistingAdmin
            ? userDataForAdminMutation?.first
            : newAdminPayload.first,
          lastName: showAddExistingAdmin
            ? userDataForAdminMutation?.last
            : newAdminPayload.last,
          email: showAddExistingAdmin
            ? userDataForAdminMutation?.email
            : searchEmail,
        },
        licenseType: activationContext.invoiceDetails?.type,
        ...(activationContext.licenseData?.school?.status &&
        activationContext.licenseData?.school?.status !== "none"
          ? { integrations: activationContext.licenseData.school.integrations }
          : {}),
      };
      const response = await axios.post(
        deltamathAPI() +
          `/manager_new/invoices/create_admin/${activationContext.invoiceDetails?.quoteNumber}`,
        body
      );
      return response.data;
    },
    onSuccess: (res) => {
      toastContext.addToast({
        status: "Success",
        message: res.message || "Admin created successfully",
      });
    },
    onError: (e) => {
      toastContext.addToast({
        status: "Error",
        message:
          (e as any)?.response?.data?.message ||
          e.message ||
          "There was an error with the request: " + e,
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["admins", activationContext.invoiceDetails?.quoteNumber],
      });
      queryClient.removeQueries({ queryKey: ["addAdmin"] });
      // setRefetchInvoiceOnModalClose(true);
      queryClient.invalidateQueries({
        queryKey: ["invoice", activationContext.invoiceDetails?.quoteNumber],
      });
    },
  });

  const handleAdminCreate = (e: FormEvent) => {
    e.preventDefault();
    if (
      searchEmail.length > 0 &&
      newAdminPayload.first.length > 0 &&
      newAdminPayload.last.length > 0
    ) {
      createAdmin();
    } else {
      toastContext.addToast({
        status: "Error",
        message:
          "Please enter email, first name, and last name for the new admin.",
      });
    }
  };

  const handleCancel = () => {
    queryClient.removeQueries({ queryKey: ["addAdmin"] });
    setMutationAction("none");
  };

  useEffect(() => {
    if (userDataForAdminMutation?.existingIsTeacherOnly) {
      setNewAdminPayload({
        first: userDataForAdminMutation?.first || "",
        last: userDataForAdminMutation?.last || "",
      });
    } else {
      setNewAdminPayload({ first: "", last: "" });
    }
  }, [userDataForAdminMutation?.existingIsTeacherOnly]);

  return (
    <Fragment>
      {showAddExistingAdmin && userDataForAdminMutation && (
        <div className="flex flex-row justify-between gap-2">
          {siteType === "school"
            ? userDataForAdminMutation?.account_type?.includes("school")
              ? `${
                  userDataForAdminMutation.email
                } is already an admin at at least one other school (${userDataForAdminMutation.schoolIDs?.join(
                  ", "
                )}). By making ${
                  userDataForAdminMutation.email
                } the primary admin of this school, any current primary admins will be downgraded to a regular admin unless they are also a primary admin of another school.`
              : `${userDataForAdminMutation.email} is already a district admin (${userDataForAdminMutation.districtID}), which means they cannot be added as primary admin at this school. You can reach out to the customer stating: "Due to the fact that ${userDataForAdminMutation.email} is a district admin elsewhere, they are unable to be listed as a primary admin at ${activationContext.invoiceDetails?.name}. While we can add them as a regular admin to ${activationContext.invoiceDetails?.name}, we will need a different person to be listed as the primary.`
            : `${userDataForAdminMutation.email} is already an admin (${
                userDataForAdminMutation.districtID
              }, ${userDataForAdminMutation.schoolIDs?.join(
                ", "
              )}), which is in a different district than this site license. You can reach out to the customer stating "Due to the fact that ${
                userDataForAdminMutation.email
              } is a district admin in a different district, they are unable to be listed as a primary admin at ${
                activationContext.invoiceDetails?.name
              }. We will need a different person to be listed as the primary." (Note: This case is rare! Please escalate to Cole/Nicole/Ben if needed.)`}
          <div className="flex grow flex-row items-center gap-2">
            {siteType === "school" && (
              <>
                {(userDataForAdminMutation?.account_type === "school" ||
                  userDataForAdminMutation?.account_type ===
                    "super_school") && (
                  <Button
                    className="text-xs"
                    onClick={createAdmin}
                    disabled={showAddNewAdminForm}
                  >
                    Primary
                  </Button>
                )}
                <Button
                  className="text-xs"
                  onClick={() =>
                    updateAdmin({
                      adminPayload: {
                        newAdminID: userDataForAdminMutation._id,
                        newAdminEmail: userDataForAdminMutation.email,
                        newAdminFirst: userDataForAdminMutation.first,
                        newAdminLast: userDataForAdminMutation.last,
                      },
                      addSchoolOnly: true,
                    })
                  }
                >
                  School
                </Button>
              </>
            )}
            <Button
              onClick={handleCancel}
              className="bg-red-500 text-xs hover:bg-red-700"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
      {showAddNewAdminForm && (
        <div className={"flex w-full flex-col gap-4"}>
          <div className="self-center">
            {userDataForAdminMutation?.existingIsTeacherOnly
              ? `A teacher account was found with this email address. Do you want to make ${newAdminPayload.first} ${newAdminPayload.last} the primary admin?`
              : `There is no account with the email ${searchEmail} in our system. Please fill in the name below.`}
          </div>
          <div className="w-full">
            <form
              className="flex w-full flex-row justify-center gap-4"
              onSubmit={handleAdminCreate}
            >
              <div className="flex flex-row items-center gap-2">
                <label htmlFor="firstName">First Name:</label>
                <input
                  className="rounded pl-2"
                  type="text"
                  id="firstName"
                  value={newAdminPayload.first}
                  onChange={(e) =>
                    setNewAdminPayload((prev) => ({
                      ...prev,
                      first: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="flex flex-row items-center gap-2">
                <label htmlFor="lastName">Last Name:</label>
                <input
                  className="rounded pl-2"
                  type="text"
                  id="lastName"
                  value={newAdminPayload.last}
                  onChange={(e) =>
                    setNewAdminPayload((prev) => ({
                      ...prev,
                      last: e.target.value,
                    }))
                  }
                />
              </div>
              <Button submit={true}>Create</Button>
              <Button
                onClick={handleCancel}
                submit={false}
                className="bg-red-500 text-xs hover:bg-red-700"
              >
                Cancel
              </Button>
            </form>
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default CreateAdmin;
